import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, ChevronDown, ChevronUp, MessageCircle, Phone, Mail } from "lucide-react";
import { useT } from "@/i18n";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";
const WHATSAPP_LINK = "https://wa.me/819045660098?text=Ol%C3%A1%2C%20preciso%20de%20suporte";

function FAQItem({ item, isOpen, onToggle, index }) {
  return (
    <div data-testid={`faq-accordion-item-${index}`} className="glass-card rounded-2xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-6 text-left" data-testid={`faq-trigger-${index}`}>
        <span className="text-base sm:text-lg font-medium text-white pr-4">{item.q}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-orange-400 flex-shrink-0" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" strokeWidth={1.5} />}
      </button>
      {isOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="px-6 pb-6">
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{item.a}</p>
        </motion.div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const t = useT();
  const s = t.supportPage;
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = s.faqs.filter((item) =>
    item.q.toLowerCase().includes(searchQuery.toLowerCase()) || item.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#020617]">
      <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center"><img src={LOGO_URL} alt="NewSky Mobile" className="h-10 sm:h-12 w-auto" /></Link>
          <Link to="/" data-testid="support-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />{s.backHome}
          </Link>
        </div>
      </nav>
      <section className="pt-32 pb-16 px-4 sm:px-6 relative">
        <div className="hero-gradient-orb-1 -top-32 left-1/4" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-orange-400 mb-4">{s.overline}</p>
            <h1 data-testid="support-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white mb-6">{s.title}</h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto mb-10">{s.sub}</p>
            <div className="relative max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="support-search-input" placeholder={s.searchPh}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition-all text-base" />
            </div>
          </motion.div>
        </div>
      </section>
      <section className="pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-xl sm:text-2xl font-medium text-white mb-8">{s.faqTitle}</h2>
            <div className="space-y-3">
              {filteredFaqs.length > 0 ? filteredFaqs.map((item, index) => (
                <FAQItem key={index} item={item} index={index} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} />
              )) : (
                <div className="text-center py-12"><p className="text-sm text-slate-500">{s.noResults} "{searchQuery}"</p></div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
      <section className="pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-medium text-white mb-8">{s.needHelp}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="support-whatsapp" className="glass-card rounded-2xl p-6 text-center group">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors"><MessageCircle className="w-5 h-5 text-orange-400" strokeWidth={1.5} /></div>
              <p className="text-sm font-medium text-white">{s.whatsapp}</p><p className="text-xs text-slate-500 mt-1">{s.whatsappSub}</p>
            </a>
            <a href="tel:0345501426" data-testid="support-phone" className="glass-card rounded-2xl p-6 text-center group">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors"><Phone className="w-5 h-5 text-orange-400" strokeWidth={1.5} /></div>
              <p className="text-sm font-medium text-white">{s.phone}</p><p className="text-xs text-slate-500 mt-1">03-4550-1426</p>
            </a>
            <a href="mailto:internet@newsky.jp" data-testid="support-email" className="glass-card rounded-2xl p-6 text-center group">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors"><Mail className="w-5 h-5 text-orange-400" strokeWidth={1.5} /></div>
              <p className="text-sm font-medium text-white">{s.emailLabel}</p><p className="text-xs text-slate-500 mt-1">internet@newsky.jp</p>
            </a>
          </div>
        </div>
      </section>
      <footer className="border-t border-white/5 py-8"><div className="max-w-7xl mx-auto px-4 sm:px-6 text-center"><p className="text-sm text-slate-600">&copy; {new Date().getFullYear()} NewSky Mobile</p></div></footer>
    </div>
  );
}
